import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  IEntityPermissions,
  IGenericRequestParameters,
  InferSortByOptions,
  ISignableEntity,
  StringIndexedDict,
} from "../../../api/GenericTypes";
import SearchInputWithOptions from "../../../common/forms/SearchInputWithOptions/SearchInputWithOptions";
import TableView, { TableViewLabel } from "../../../common/panels/TableView/TableView";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import { EntityTableTabs } from "../../../common/tables/Tabs/EntityTableTabs";
import { GenericEntity, GenericEntityComponentProps } from "../GenericComponents";
import { genericEntityConstants, GenericEntityConstantsEntities } from "../../../api/GenericConstants";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import {
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import {
  switchSamplesDefaultSortState,
  useSamplesTableColumns,
  useSamplesTableDefaults,
} from "../../../samples/Table/SamplesTable";
import {
  switchDatasetsDefaultSortState,
  useDatasetsTableColumns,
  useDatasetsTableDefaults,
} from "../../../Dataset/Table/DatasetsTable";
import { SampleSearchTermOptions } from "../../../api/Samples";
import { DatasetSearchTermOptions } from "../../../api/Datasets";
import { GenericEntityFilterForm } from "./GenericEntityFilterForm";
import {
  GenericVirtualizedTableFunctionRef,
  GenericVirtualizedTableProps,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import {
  switchProjectsDefaultSortState,
  useProjectsTableColumns,
  useProjectsTableDefaults,
} from "../../../Projects/Table/ProjectsTable";
import {
  switchPersonsDefaultSortState,
  usePersonsTableColumns,
  usePersonsTableDefaults,
} from "../../../Persons/Table/PersonsTable";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import { LinkButton } from "../../../common/buttons/LinkButton/LinkButton";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { getCloneLink, getEditLink, getIndexRoute } from "../../../main/Routing";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import { Button } from "../../../common/buttons/Button/Button";
import { MoreDropdown } from "../../../common/buttons/MoreDropdown/MoreDropdown";
import { ExportCsvButton } from "../../../common/tables/ExportCsvButton/ExportCsvButton";
import { useEntityApi } from "../../../api/useEntityApi";
import { AlertModal, AlertModalOnProceedProps } from "../../../common/modals/AlertModal/AlertModal";
import { NotAllowed } from "../../../common/misc/UIconstants";
import { useQueryClient } from "@tanstack/react-query";
import { AddCustomTypeEntity } from "../../../Customization/CustomTypes/generics/AddCustomTypeEntity";
import { GenericBooleanStateProvider } from "../../../common/modals/Modal/GenericModal";
import { ExclusiveDropdown } from "../../../common/buttons/ExclusiveDropdown/ExclusiveDropdown";
import { DatasetsBulkEditDropDownWrapper } from "../../../Dataset/Table/DatasetsBulkEditDropDown";
import { SamplesBulkEditDropDownWrapper } from "../../../samples/Table/SamplesBulkEditDropDown";
import { CustomTypeEntityType } from "../../../api/CustomTypes/CustomTypesBase";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import {
  switchInventoryItemsDefaultSortState,
  useInventoryItemsTableColumns,
  useInventoryItemsTableDefaults,
} from "../../../Inventories/Table/InventoryItemsTable";
import {
  switchMethodsDefaultSortState,
  useMethodsTableColumns,
  useMethodsTableDefaults,
} from "../../../Methods/Table/MethodsTable";
import {
  switchCustomFieldsDefaultSortState,
  useCustomFieldsTableColumns,
  useCustomFieldsTableDefaults,
} from "../../../Customization/CustomFields/Table/CustomFieldsTable";
import {
  switchCustomTypesDefaultSortState,
  useCustomTypesTableColumns,
  useCustomTypesTableDefaults,
} from "../../../Customization/CustomTypes/Table/CustomTypesTable";
import {
  switchAuditLogDefaultSortState,
  useAuditLogsTableColumns,
  useAuditLogsTableDefaults,
} from "../../../AuditLog/AuditLogTable";
import {
  switchAnnouncementsDefaultSortState,
  useAnnouncementsTableColumns,
  useAnnouncementsTableDefaults,
} from "../../../Announcements/Table/AnnouncementsTable";
import {
  switchRolesDefaultSortState,
  useRolesTableColumns,
  useRolesTableDefaults,
} from "../../../Roles/Table/RolesTable";
import {
  switchAttachmentDefaultSortState,
  useAttachmentsTableColumns,
  useAttachmentsTableDefaults,
} from "../../../Attachments/Table/AttachmentsTable";
import {
  switchLabNotebookEntriesDefaultSortState,
  useLabNotebookEntriesTableColumns,
  useLabNotebookEntriesTableDefaults,
} from "../../../ELN/LabNotebookEntries/Table/LabNotebookEntriesTable";
import {
  switchLabNotebookExperimentsDefaultSortState,
  useLabNotebookExperimentsTableColumns,
  useLabNotebookExperimentsTableDefaults,
} from "../../../ELN/LabNotebookExperiments/Table/LabNotebookExperimentsTable";
import {
  switchLabNotebookTemplatesDefaultSortState,
  useLabNotebookTemplatesTableColumns,
  useLabNotebookTemplatesTableDefaults,
} from "../../../ELN/LabNotebookTemplates/Table/LabNotebookTemplatesTable";
import {
  switchLabNotebooksDefaultSortState,
  useLabNotebooksTableColumns,
  useLabNotebooksTableDefaults,
} from "../../../ELN/LabNotebooks/Table/LabNotebooksTable";
import { CSSProperties } from "remirror";
import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import { AuditLogSearchTermOptions } from "../../../api/AuditLog";
import { ToggleSwitch } from "../../../common/buttons/ToggleSwitch/ToggleSwitch";
import { useSigninRedirectWithAction } from "../../../main/LoginPage/OidcRedirect";
import { SelectFormField } from "../../../common/formfields/SelectFormField";
import { TextareaFormField } from "../../../common/formfields/TextareaFormField";
import { Signature, SignatureTypes } from "../../../api/Signature";
import styles from "../../../common/modals/AlertModal/AlertModal.module.css";
import { useForm } from "react-hook-form";
import { ToggleButtonComponent } from "../../../ViewerUIElements/ToggleButtonComponent";
import { useEntityDetail } from "../../../api/BaseEntityApi";
import { NestedDropdown } from "../../../common/buttons/NestedDropdown/NestedDropdown";

const useEntityTableDefaults = ({ entityTypeId }: { entityTypeId: GenericEntityConstantsEntities }) => {
  switch (entityTypeId) {
    case "announcements":
      return useAnnouncementsTableDefaults;
    case "attachments":
      return useAttachmentsTableDefaults;
    case "auditLogs":
      return useAuditLogsTableDefaults;
    case "customFields":
      return useCustomFieldsTableDefaults;
    case "customTypes":
      return useCustomTypesTableDefaults;
    case "datasets":
      return useDatasetsTableDefaults;
    case "inventoryItems":
      return useInventoryItemsTableDefaults;
    case "methods":
      return useMethodsTableDefaults;
    case "notebookEntries":
      return useLabNotebookEntriesTableDefaults;
    case "notebookExperiments":
      return useLabNotebookExperimentsTableDefaults;
    case "notebookTemplates":
      return useLabNotebookTemplatesTableDefaults;
    case "notebooks":
      return useLabNotebooksTableDefaults;
    case "persons":
      return usePersonsTableDefaults;
    case "projects":
      return useProjectsTableDefaults;
    case "samples":
      return useSamplesTableDefaults;
    case "roles":
      return useRolesTableDefaults;
    default:
      return null;
  }
};

const useEntityTableDefaultSortState = ({ entityTypeId }: { entityTypeId: GenericEntityConstantsEntities }) => {
  switch (entityTypeId) {
    case "announcements":
      return switchAnnouncementsDefaultSortState;
    case "attachments":
      return switchAttachmentDefaultSortState;
    case "auditLogs":
      return switchAuditLogDefaultSortState;
    case "customFields":
      return switchCustomFieldsDefaultSortState;
    case "customTypes":
      return switchCustomTypesDefaultSortState;
    case "datasets":
      return switchDatasetsDefaultSortState;
    case "inventoryItems":
      return switchInventoryItemsDefaultSortState;
    case "methods":
      return switchMethodsDefaultSortState;
    case "notebookEntries":
      return switchLabNotebookEntriesDefaultSortState;
    case "notebookExperiments":
      return switchLabNotebookExperimentsDefaultSortState;
    case "notebookTemplates":
      return switchLabNotebookTemplatesDefaultSortState;
    case "notebooks":
      return switchLabNotebooksDefaultSortState;
    case "persons":
      return switchPersonsDefaultSortState;
    case "projects":
      return switchProjectsDefaultSortState;
    case "samples":
      return switchSamplesDefaultSortState;
    case "roles":
      return switchRolesDefaultSortState;
    default:
      return null;
  }
};

const useEntityTableColumns = ({ entityTypeId }: { entityTypeId: GenericEntityConstantsEntities }) => {
  switch (entityTypeId) {
    case "announcements":
      return useAnnouncementsTableColumns;
    case "attachments":
      return useAttachmentsTableColumns;
    case "auditLogs":
      return useAuditLogsTableColumns;
    case "customFields":
      return useCustomFieldsTableColumns;
    case "customTypes":
      return useCustomTypesTableColumns;
    case "datasets":
      return useDatasetsTableColumns;
    case "inventoryItems":
      return useInventoryItemsTableColumns;
    case "methods":
      return useMethodsTableColumns;
    case "notebookEntries":
      return useLabNotebookEntriesTableColumns;
    case "notebookExperiments":
      return useLabNotebookExperimentsTableColumns;
    case "notebookTemplates":
      return useLabNotebookTemplatesTableColumns;
    case "notebooks":
      return useLabNotebooksTableColumns;
    case "persons":
      return usePersonsTableColumns;
    case "projects":
      return useProjectsTableColumns;
    case "samples":
      return useSamplesTableColumns;
    case "roles":
      return useRolesTableColumns;
    default:
      return null;
  }
};

const useEntitySearchTermOptions = ({ entityTypeId }: { entityTypeId: GenericEntityConstantsEntities }) => {
  switch (entityTypeId) {
    case "auditLogs":
      return AuditLogSearchTermOptions;
    case "datasets":
      return DatasetSearchTermOptions;
    case "samples":
      return SampleSearchTermOptions;
    default:
      return null;
  }
};

const useEntityFilterIndicator = ({
  entityTypeId,
  currentTab,
}: {
  entityTypeId: GenericEntityConstantsEntities;
  currentTab?: string;
}) => {
  switch (entityTypeId) {
    case "auditLogs":
      return {
        searchTermIncludeIds: () => false,
        searchTermIncludeNames: () => false,
        searchTermIncludeUuids: () => false,
      };
    case "datasets":
      return {
        includeParsingState: () => false,
        includeSoftDeleted: (value: boolean) => value === true,
        includeViewableEntities: (value: boolean) => value === true,
        isSoftDeleted: () => false,
        participatedPersonIds: () => false,
        ...(currentTab === "claimed" && { isClaimed: () => false }),
        includeUnclaimed: (value: boolean) => false,
        searchTermIncludeIds: () => false,
        searchTermIncludeNames: () => false,
        searchTermIncludeNotes: () => false,
        searchTermIncludeParameters: () => false,
        searchTermIncludePaths: () => false,
        searchTermIncludeUuids: () => false,
      };
    case "inventoryItems":
      return {};
    case "persons":
      return {
        includeSystemUsers: (value: boolean) => !!value,
        hasAccount: (value: boolean) =>
          currentTab !== "activeUsers" && currentTab !== "disabledUsers" ? !!value : false,
        isAccountEnabled: (value: boolean) =>
          currentTab !== "activeUsers" && currentTab !== "disabledUsers" ? !!value : false,
        includeSoftDeleted: (value: boolean) => !!value,
        isSoftDeleted: (value: boolean) => !!value,
      };
    case "projects":
      return {};
    case "samples":
      return {
        includeSoftDeleted: (value: boolean) => value === true,
        isSoftDeleted: () => false,
        participatedPersonIds: () => false,
        searchTermIncludeIds: () => false,
        searchTermIncludeNames: () => false,
        searchTermIncludeNotes: () => false,
        searchTermIncludeUuids: () => false,
      };
    default:
      return {
        includeSoftDeleted: (value: boolean) => value === true,
        isSoftDeleted: () => false,
        participatedPersonIds: () => false,
        searchTermIncludeIds: () => false,
        searchTermIncludeNames: () => false,
        searchTermIncludeNotes: () => false,
        searchTermIncludeUuids: () => false,
      };
  }
};

const useCustomTypedEntityAddButtonProps = ({ entityTypeId }: { entityTypeId: GenericEntityConstantsEntities }) => {
  const { canCreate } = useEntityPermissions({ entityTypeId });
  switch (entityTypeId) {
    case "datasets":
      return {
        canCreateEntityPermission: canCreate,
        entityType: "Dataset" as CustomTypeEntityType,
      };
    case "samples":
      return {
        canCreateEntityPermission: canCreate,
        entityType: "Sample" as CustomTypeEntityType,
      };
    default:
      return null;
  }
};

const useGenericEntityTableBulkEdit = ({ entityTypeId }: { entityTypeId: GenericEntityConstantsEntities }) => {
  switch (entityTypeId) {
    case "datasets":
      return DatasetsBulkEditDropDownWrapper;
    case "samples":
      return SamplesBulkEditDropDownWrapper;
    default:
      return null;
  }
};

export interface GenericEntityTableProps<
  Entity extends GenericEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
  FilterbarProps extends StringIndexedDict = {},
> extends Partial<GenericVirtualizedTableProps<Entity, Filters>> {
  hideTabs?: boolean;
  hideHeaderControls?: boolean;
  hideTableControls?: boolean;
  hideSidebar?: boolean;
  hideSearch?: boolean;
  hideLockButtons?: boolean;
  hideDeleteButton?: boolean;
  hideTitle?: boolean;
  tabStoreKeyOverride?: string;
  hideCustomTypedEntityAddButton?: boolean;
  forcedFilters?: Filters;
  countCallback?: (count: number) => void;
  selectionCallback?: (selection: Set<Entity["id"]>) => void;
  selectionPermissionsCallback?: (permissions: IEntityPermissions) => void;
  onRowClickPreset?: "select" | "link";
  controlsHeader?: (props: { currentTab?: string }) => React.ReactNode; // full override
  additionalControlsHeader?: (props: { currentTab?: string }) => React.ReactNode; // add to default controls
  controlsTable?: (props: GenericEntityTableControlProps<Entity, Filters>) => React.ReactNode; // full override
  additionalControlsTableOnScreen?: (props: GenericEntityTableControlProps<Entity, Filters>) => React.ReactNode; // add to default controls
  additionalControlsTableDropdown?: (props: GenericEntityTableControlProps<Entity, Filters>) => React.ReactNode; // add to default controls
  refetchInterval?: number; // in milliseconds
  readonly?: boolean;
  containerStyle?: CSSProperties;
  sidebarDefaultSize?: number;
  /**
   * Override the default columns settings. Requqires a memoized callback that returns the new columns settings.
   * @param defaultColumns ColumnsSettings<Entity> | null
   * @returns ColumnsSettings<Entity> | null
   */
  columnOverrideCallback?: (defaultColumns: ColumnsSettings<Entity> | null) => ColumnsSettings<Entity> | null;
  additionalFilterbarComponentProps?: FilterbarProps;
  preventIndexPermissionCheck?: boolean;
}

export const GenericEntityTable = <
  Entity extends GenericEntity & StringIndexedDict,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
  FilterForm extends StringIndexedDict,
  FilterbarProps extends StringIndexedDict = {},
>({
  entityTypeId,
  componentProps,
}: GenericEntityComponentProps<Entity, GenericEntityTableProps<Entity, Filters, FilterbarProps>>) => {
  const { route } = useContext(SessionContext);

  // Entity hooks and constants
  const { entityConstants, filtersTranslator, fieldLabels } = genericEntityConstants[entityTypeId];
  const searchTermOptions = useEntitySearchTermOptions({ entityTypeId });
  const filterIndicator = useEntityFilterIndicator({ entityTypeId });
  const entityTableDefaults = useEntityTableDefaults({ entityTypeId });
  const { defaults, tabStoreDefaults } = entityTableDefaults
    ? entityTableDefaults({ fieldLabels: fieldLabels as any })
    : { defaults: null, tabStoreDefaults: null };
  const { resultsCount, onCountChange, onSelectionChange, onSelectionPermissions, selection, selectionPermissions } =
    useGenericVirtualizedTable<Entity>();
  const customTypedEntityAddButtonProps = useCustomTypedEntityAddButtonProps({ entityTypeId });

  const {
    filters,
    forcedFilters,
    sidebarFilters,
    columnSetting,
    columnWidths,
    customTabs,
    fixedTabs,
    dispatchTabStore,
    currentTab,
    tabsLoading,
    tabsModified,
  } = useTabStore<Entity, Filters, FilterForm>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults as any,
    keyOverride: componentProps?.tabStoreKeyOverride,
  });

  const { functionRef, onTabChange, sort, setSort, searchValue, setSearchValue, debouncedSearchValue } =
    useGenericVirtualizedTableTabs({
      filters,
      switchSortState: useEntityTableDefaultSortState({ entityTypeId }) as any,
      dispatchTabStore,
      currentTab,
      tabsLoading,
    });

  const entityTableColumns = useEntityTableColumns({ entityTypeId });
  const { columns, columnSettings } = entityTableColumns
    ? entityTableColumns({
        entityConstants,
        fieldLabels: fieldLabels as any,
        defaults: defaults as any,
        filters: filters as any,
        sort: sort as any,
        setSort: setSort as any,
      })
    : { columns: [], columnSettings: {} };

  const memoizedColumnDefaults = useMemo(
    () => componentProps?.columnOverrideCallback?.(columnSettings ?? (defaults as any)) ?? columnSettings ?? defaults,
    [columnSettings, componentProps, defaults]
  );

  const memoizedFilters = useMemo(
    () => ({ ...filters, ...forcedFilters, ...componentProps?.forcedFilters }),
    [filters, forcedFilters, componentProps?.forcedFilters]
  );

  // Callbacks
  const countCallback = useCallback(
    (count: number | undefined) => {
      onCountChange(count);
      componentProps?.countCallback?.(count ?? 0);
    },
    [componentProps, onCountChange]
  );

  const selectionCallback = useCallback(
    (selection: Set<Entity["id"]>) => {
      onSelectionChange(selection);
      componentProps?.selectionCallback?.(selection);
    },
    [componentProps, onSelectionChange]
  );

  const selectionPermissionsCallback = useCallback(
    (permissions?: IEntityPermissions) => {
      onSelectionPermissions(permissions);
      componentProps?.selectionPermissionsCallback?.(permissions ?? { canEdit: false, canDelete: false });
    },
    [componentProps, onSelectionPermissions]
  );

  // Will be ignored if onRowCLick is manually overridden via props
  const onRowClick = useMemo(() => {
    switch (componentProps?.onRowClickPreset) {
      case "select":
        // Select row
        return (row: Entity) => {
          functionRef.current?.setSelection((prevState) => {
            var newSelection = new Set(Array.from(prevState));
            newSelection.has(row.id) ? newSelection.delete(row.id) : newSelection.add(row.id);
            return newSelection;
          });
          functionRef.current?.forceUpdate();
        };
      // Link to detail page
      default:
        return;
    }
  }, [componentProps?.onRowClickPreset, functionRef]);

  // automatically refetch if props.refetchInterval is set
  const { invalidateQueries } = useQueryClient();
  useEffect(() => {
    if (componentProps?.refetchInterval) {
      const interval = setInterval(() => {
        invalidateQueries({ queryKey: [entityConstants.resource, "list"] });
        invalidateQueries({ queryKey: [entityConstants.resource, "count"] });
      }, componentProps.refetchInterval);
      return () => clearInterval(interval);
    }
  }, [componentProps?.refetchInterval, entityConstants.resource, invalidateQueries]);

  const [isRefetching, setIsRefetching] = useState<boolean>(false);

  const useInfiniteQueryOptions = useMemo(
    () => ({
      ...componentProps?.useInfiniteQueryOptions,
      ...(componentProps?.useInfiniteQueryOptions?.queryKey
        ? {
            queryKey: componentProps.useInfiniteQueryOptions.queryKeyIncludeFilters
              ? [componentProps.useInfiniteQueryOptions.queryKey, memoizedFilters]
              : componentProps.useInfiniteQueryOptions.queryKey,
          }
        : {}),
    }),
    [componentProps?.useInfiniteQueryOptions, memoizedFilters]
  );

  const { canReadIndex } = useEntityPermissions({ entityTypeId });
  if (!componentProps?.preventIndexPermissionCheck && !canReadIndex) return NotAllowed;
  return (
    <TableView style={componentProps?.containerStyle}>
      {!componentProps?.hideTitle && !componentProps?.hideHeaderControls && (
        <TableView.Head>
          {!componentProps?.hideTitle && (
            <TableView.Head.Label>
              <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
            </TableView.Head.Label>
          )}
          {!componentProps?.hideHeaderControls && (
            <TableView.Head.Controls>
              {componentProps?.controlsHeader?.({ currentTab })}
              {!componentProps?.hideCustomTypedEntityAddButton && !!customTypedEntityAddButtonProps && (
                <AddCustomTypeEntity entityConstants={entityConstants} {...customTypedEntityAddButtonProps} />
              )}
            </TableView.Head.Controls>
          )}
        </TableView.Head>
      )}
      <TableView.Body>
        {!componentProps?.hideSidebar && (
          <TableView.Body.Sidebar defaultSize={componentProps?.sidebarDefaultSize}>
            <GenericEntityFilterForm
              entityTypeId={entityTypeId}
              componentProps={{
                ...componentProps?.additionalFilterbarComponentProps,
                initialValues: sidebarFilters,
                dispatchTabStore: dispatchTabStore,
                tabsLoading: tabsLoading,
                currentTab: currentTab,
              }}
            />
          </TableView.Body.Sidebar>
        )}
        <TableView.Body.Content>
          {!componentProps?.hideTabs && (
            <EntityTableTabs
              currentTab={currentTab}
              onTabChange={onTabChange}
              fixedTabs={fixedTabs}
              filters={memoizedFilters}
              sidebarFilters={sidebarFilters}
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              customTabs={customTabs}
              dispatchTabStore={dispatchTabStore}
              tabsLoading={tabsLoading}
              tabsModified={tabsModified}
            />
          )}
          <EntityTable>
            {!!componentProps?.hideSearch && !!componentProps.hideHeaderControls ? (
              <div></div>
            ) : (
              <EntityTable.Controls
                style={{
                  borderTop: "0px",
                  borderRadius: "0px",
                }}
              >
                {componentProps?.controlsTable?.({
                  entityTypeId,
                  selection,
                  selectionPermissions,
                  tableFunctionRef: functionRef,
                  currentTab,
                }) ?? (
                  <>
                    {!componentProps?.hideSearch && (
                      <>
                        {/* Filter indicator */}
                        <EntityFilterIndicator<Entity, Filters>
                          filters={filters}
                          translatorConsts={filtersTranslator as any}
                          excludeFilters={filterIndicator as any}
                        />
                        {/* Searchbar */}
                        {!searchTermOptions ? (
                          <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
                        ) : (
                          <SearchInputWithOptions
                            searchTermOptions={searchTermOptions as any}
                            filters={filters}
                            setFilters={async (newFilters) => {
                              await dispatchTabStore({
                                type: "setTab",
                                options: { keepPrevious: true },
                                payload: { settings: { filters: { searchTerm: debouncedSearchValue, ...newFilters } } },
                              });
                            }}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                          />
                        )}
                      </>
                    )}

                    {!componentProps?.hideTableControls && (
                      <>
                        {/* On screen controls */}
                        <div>
                          <Button
                            loading={isRefetching}
                            onClick={() => functionRef.current?.refetch()}
                            className="btn btn-default"
                            title="Refresh the table content"
                          >
                            <LucideIcon name="refresh-ccw" /> Refresh
                          </Button>
                        </div>
                        {!componentProps?.readonly && (
                          <div>
                            <GenericEntityTableEditButton
                              entityTypeId={entityTypeId}
                              selection={selection}
                              selectionPermissions={selectionPermissions}
                              tableFunctionRef={functionRef}
                              currentTab={currentTab}
                              filters={memoizedFilters}
                            />
                          </div>
                        )}

                        {/* Additional on screen controls */}
                        {componentProps?.additionalControlsTableOnScreen?.({
                          entityTypeId,
                          selection,
                          selectionPermissions,
                          tableFunctionRef: functionRef,
                          currentTab,
                        })}

                        {/* Dropdown controls */}
                        {(!componentProps?.readonly ||
                          !!componentProps.additionalControlsTableDropdown ||
                          !componentProps.disableCheckboxes) && (
                          <div>
                            <MoreDropdown drop="right" btn="btn btn-ghost-secondary" contentStyle={{ padding: "2px" }}>
                              {!componentProps?.readonly && (
                                <LinkButton
                                  className="btn btn-ghost-secondary btn-block"
                                  style={{ textAlign: "left" }}
                                  title={`Clone the selected ${entityConstants.entitySingular}`}
                                  disabled={selection.size !== 1}
                                  linkTo={route(
                                    getCloneLink(entityConstants.frontendIndexRoute, Array.from(selection)[0])
                                  )}
                                >
                                  <LucideIcon name="copy" /> Clone
                                </LinkButton>
                              )}
                              {/* Additional dropdown controls */}
                              {!!componentProps?.additionalControlsTableDropdown && (
                                <>
                                  {componentProps?.additionalControlsTableDropdown?.({
                                    entityTypeId,
                                    selection,
                                    selectionPermissions,
                                    tableFunctionRef: functionRef,
                                    currentTab,
                                  })}
                                </>
                              )}
                              {(!componentProps?.readonly || !!componentProps?.additionalControlsTableDropdown) && (
                                <hr style={{ margin: "2px 0", borderColor: "var(--gray-200)" }} />
                              )}
                              {!componentProps?.disableCheckboxes && (
                                <ExportCsvButton
                                  className="btn btn-ghost-secondary btn-block"
                                  style={{ textAlign: "left" }}
                                  entityConstants={entityConstants}
                                  columnSettings={columnSetting}
                                  filters={{
                                    ids: selection.size > 0 ? Array.from(selection) : undefined,
                                    ...memoizedFilters,
                                    searchTerm: debouncedSearchValue,
                                  }}
                                  disabled={selection.size === 0}
                                />
                              )}
                              {!componentProps?.readonly && (
                                <>
                                  {!componentProps?.hideLockButtons && (
                                    <>
                                      <hr style={{ margin: "2px 0", borderColor: "var(--gray-200)" }} />
                                      <GenericEntityTableLockButton
                                        entityTypeId={entityTypeId}
                                        selection={selection}
                                        selectionPermissions={selectionPermissions}
                                        tableFunctionRef={functionRef}
                                        currentTab={currentTab}
                                      />
                                      <GenericEntityTableSignButton
                                        entityTypeId={entityTypeId}
                                        selection={selection}
                                        selectionPermissions={selectionPermissions}
                                        tableFunctionRef={functionRef}
                                        currentTab={currentTab}
                                      />
                                      <GenericEntityTableUnlockButton
                                        entityTypeId={entityTypeId}
                                        selection={selection}
                                        selectionPermissions={selectionPermissions}
                                        tableFunctionRef={functionRef}
                                        currentTab={currentTab}
                                      />
                                    </>
                                  )}
                                  {!componentProps?.hideDeleteButton && (
                                    <>
                                      <hr style={{ margin: "2px 0", borderColor: "var(--gray-200)" }} />
                                      <GenericEntityTableDeleteButton
                                        entityTypeId={entityTypeId}
                                        selection={selection}
                                        selectionPermissions={selectionPermissions}
                                        tableFunctionRef={functionRef}
                                        currentTab={currentTab}
                                      />
                                    </>
                                  )}
                                </>
                              )}
                            </MoreDropdown>
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </EntityTable.Controls>
            )}
            <EntityTable.Body<Entity, Filters>
              showPermissionColumn
              columnSelect
              onRowClick={onRowClick}
              {...componentProps}
              useInfiniteQueryOptions={useInfiniteQueryOptions}
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns as any}
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={memoizedColumnDefaults as any}
              dispatchTabStore={dispatchTabStore as any}
              setResultsCount={countCallback}
              onSelectionChange={selectionCallback}
              onSelectionPermissions={selectionPermissionsCallback}
              loading={tabsLoading}
              onRefetch={setIsRefetching}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};

export interface GenericEntityTableControlProps<
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
> {
  entityTypeId: GenericEntityConstantsEntities;
  selection: Set<Entity["id"]>;
  selectionPermissions: IEntityPermissions | undefined;
  tableFunctionRef: React.RefObject<GenericVirtualizedTableFunctionRef<Entity>>;
  currentTab: string;
  filters?: Filters;
}

export const GenericEntityTableEditButton = <
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
>({
  entityTypeId,
  selection,
  selectionPermissions,
  tableFunctionRef,
  currentTab,
  filters,
}: GenericEntityTableControlProps<Entity, Filters>) => {
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const { route, session } = useContext(SessionContext);

  const { bulkRestoreMutationAsync: bulkRestoreEntities, isLoadingBulkRestoreMutation } = useEntityApi<Entity>(
    entityConstants.resource
  );

  const GenericEntityTableBulkEdit = useGenericEntityTableBulkEdit({ entityTypeId });

  return (
    <>
      <div>
        {/* Edit is only available for untrashed entities */}
        {currentTab !== "trash" ? (
          // Bulk edit is only available for defined entities
          GenericEntityTableBulkEdit ? (
            <GenericBooleanStateProvider>
              {({ isTrue: showModal, setIsTrue: setShowModal }) => (
                <ExclusiveDropdown
                  show={showModal}
                  setShow={setShowModal}
                  disabled={
                    selection.size === 0 ||
                    (session?.features.development_mode ? false : selection.size > 1000) ||
                    !selectionPermissions?.canEdit
                  }
                  title={`${
                    !selectionPermissions?.canEdit
                      ? `Insufficient permissions to edit selected ${entityConstants.entityPlural}`
                      : selection.size > 1000
                        ? `Editing is only allowed for max. 1000 ${entityConstants.entityPlural} at once`
                        : `Edit`
                  }`}
                  btnCls={"btn btn-default"}
                  btnLabel={`${currentTab === "unclaimed" ? "Edit / Claim" : "Edit"}`}
                  icon={<LucideIcon name="square-pen" />}
                >
                  {GenericEntityTableBulkEdit({
                    selection: selection,
                    onSuccess: () => {
                      tableFunctionRef.current?.resetSelection();
                      setShowModal(false);
                    },
                    onClose: () => {
                      setShowModal(false);
                    },
                    filters: filters as any,
                    entityConstants,
                  })}
                </ExclusiveDropdown>
              )}
            </GenericBooleanStateProvider>
          ) : (
            <LinkButton
              linkTo={route(getEditLink(entityConstants.frontendIndexRoute, Array.from(selection)[0]))}
              className="btn btn-default"
              disabled={selection.size !== 1 || !selectionPermissions?.canEdit}
              title={`Edit the selected ${entityConstants.entitySingular}`}
            >
              <LucideIcon name="square-pen" /> Edit
            </LinkButton>
          )
        ) : (
          <Button
            className="btn btn-success"
            title={`${
              !selectionPermissions?.canDelete
                ? `Insufficient permissions to delete selected ${entityConstants.entityPlural}`
                : `Restore the selected ${entityConstants.entityPlural}`
            }`}
            disabled={selection.size === 0 || !selectionPermissions?.canEdit}
            onClick={async () => {
              await bulkRestoreEntities({ ids: Array.from(selection) }).catch(() => {});
            }}
            loading={isLoadingBulkRestoreMutation}
          >
            <LucideIcon name="refresh-ccw" /> Restore from Trash
          </Button>
        )}
      </div>
    </>
  );
};

export const GenericEntityTableDeleteButton = <
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
>({
  entityTypeId,
  selection,
  selectionPermissions,
  tableFunctionRef,
  currentTab,
  alwaysDeletePermanently = false,
}: GenericEntityTableControlProps<Entity, Filters> & { alwaysDeletePermanently?: boolean }) => {
  const { session } = useContext(SessionContext);

  const { entityConstants } = genericEntityConstants[entityTypeId];

  const [showModal, setShowModal] = useState<boolean>(false);

  const { bulkDeleteMutationAsync: deleteEntities, isLoadingBulkDeleteMutation } = useEntityApi<Entity>(
    entityConstants.resource
  );

  const [deletePermanentlyControl, setDeletePermanentlyControl] = useState<boolean>(alwaysDeletePermanently);
  const deletePermanently = currentTab === "trash" || deletePermanentlyControl;

  const hasPermission = deletePermanently ? selectionPermissions?.canDelete : selectionPermissions?.canEdit;

  return (
    <>
      <div>
        <Button
          className={`btn btn-ghost-${deletePermanently ? "danger" : "warning"} btn-block`}
          style={{ textAlign: "left" }}
          title={`${
            !hasPermission
              ? `Insufficient permissions to ${deletePermanently ? "delete" : "trash"} the selected ${
                  entityConstants.entityPlural
                }`
              : deletePermanently
                ? `Delete the selected ${entityConstants.entityPlural}`
                : `Move the selected ${entityConstants.entityPlural} to trash`
          }`}
          disabled={selection.size === 0 || !hasPermission}
          onClick={() => setShowModal(true)}
        >
          <LucideIcon name={deletePermanently ? "trash-2" : "trash"} />{" "}
          {deletePermanently ? "Permanently delete" : "Move to trash"}
        </Button>
      </div>
      <AlertModal
        type={deletePermanently ? "danger" : "warning"}
        showModal={showModal}
        setShowModal={setShowModal}
        title={
          deletePermanently
            ? `Delete the selected ${entityConstants.entityPlural}?`
            : `Move the selected ${entityConstants.entityPlural} to trash?`
        }
        description={
          <div>
            {deletePermanently
              ? `This ${entityConstants.entitySingular} may be referenced by other entities. Deleting a ${entityConstants.entitySingular} is only possible if all references are removed.`
              : `This will move the ${entityConstants.entitySingular} into trash. It is possible to restore this ${entityConstants.entitySingular} at any time.`}

            {!!session?.features.development_mode && !alwaysDeletePermanently && (
              <>
                <hr style={{ margin: "10px 0", borderColor: "var(--gray-300)" }} />
                <label
                  className="flex align-center gap-5"
                  onClick={() => setDeletePermanentlyControl((prevState) => !prevState)}
                >
                  <ToggleSwitch checked={deletePermanently} /> Delete permanently
                </label>
              </>
            )}
          </div>
        }
        proceedLabel={deletePermanently ? "Delete" : "Move to trash"}
        onProceed={async () => {
          if (!hasPermission) return;
          await deleteEntities({
            ids: Array.from(selection),
            goBackOnSuccess: false,
            showToast: false,
            entityName: entityConstants.entityPlural,
            params: { deletePermanently },
          }).catch((e) => {});
          tableFunctionRef.current?.resetSelection();
          setShowModal(false);
        }}
        loading={isLoadingBulkDeleteMutation}
      />
    </>
  );
};

export const GenericEntityTableLockButton = <
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
>({
  entityTypeId,
  selection,
  selectionPermissions,
  currentTab,
}: GenericEntityTableControlProps<Entity, Filters>) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const { bulkLockMutationAsync, isLoadingBulkLockMutation } = useEntityApi<Entity>(entityConstants.resource);

  const queryClient = useQueryClient();

  const onSuccessCallback = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [entityConstants.resource] });
  }, [entityConstants.resource, queryClient]);

  const handleLock = useCallback(async () => {
    if (selection.size <= 0) return;
    await bulkLockMutationAsync(
      { ids: Array.from(selection), showToast: true },
      { onSuccess: onSuccessCallback }
    ).catch((e) => console.error(e));
    setShowModal(false);
  }, [bulkLockMutationAsync, onSuccessCallback, selection]);

  if (!entityConstants) return null;
  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        loading={isLoadingBulkLockMutation}
        disabled={!selectionPermissions?.canLock}
        className="btn btn-block btn-sm btn-ghost-info flex align-center gap-5"
        title="Lock selected items"
      >
        <LucideIcon name="lock" />
        Lock
      </Button>

      {showModal && (
        <AlertModal
          showModal={showModal}
          setShowModal={setShowModal}
          type="warning"
          onProceed={handleLock}
          onCancel={() => setShowModal(false)}
          title="Lock content"
          description="Are you sure you want to lock this content? This will allow other users to edit this content."
          proceedLabel="Lock"
          iconOverride="lock"
        />
      )}
    </>
  );
};

export const GenericEntityTableSignButton = <
  Entity extends GenericEntity & Partial<ISignableEntity>,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
>({
  entityTypeId,
  selection,
  selectionPermissions,
  currentTab,
}: GenericEntityTableControlProps<Entity, Filters>) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const { bulkSignMutationAsync, isLoadingBulkSignMutation } = useEntityApi<Entity>(entityConstants.resource);

  const queryClient = useQueryClient();

  const { route, session } = useContext(SessionContext);

  const onSuccessCallback = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [entityConstants.resource] });
  }, [entityConstants.resource, queryClient]);

  // sso session
  const isSSOSession = session?.type === "jwt";
  const { handleReauth } = useSigninRedirectWithAction();

  // additional elements for the signature item
  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<
    Partial<{
      signatureType: {
        id: Signature["signatureType"];
        label: Signature["signatureType"];
        value: Signature["signatureType"];
      };
      comment: string;
    }>
  >({});
  const currentSignatureType = watch("signatureType");
  const currentComment = watch("comment");

  const handleSign = useCallback(
    async (e, props?: AlertModalOnProceedProps) => {
      if (selection.size <= 0) return;
      if (!!isSSOSession) {
        handleReauth({
          actions: ["Sign"],
          entityIntIds: Array.from(selection),
          entityType: entityTypeId,
          redirectUrl: route(getIndexRoute(entityConstants.frontendIndexRoute)),
          signature: { comment: currentComment, signatureType: currentSignatureType?.value },
        });
      } else {
        await bulkSignMutationAsync(
          {
            ids: Array.from(selection),
            params: {
              credentials: props?.credentials,
              signature: { signatureType: currentSignatureType?.value, comment: currentComment },
            },
            showToast: true,
            entityName: entityConstants?.entitySingular,
          },
          { onSuccess: onSuccessCallback }
        ).catch((e) => console.error(e));
      }
      setShowModal(false);
    },
    [
      bulkSignMutationAsync,
      currentComment,
      currentSignatureType?.value,
      entityConstants?.entitySingular,
      entityConstants.frontendIndexRoute,
      entityTypeId,
      handleReauth,
      isSSOSession,
      onSuccessCallback,
      route,
      selection,
    ]
  );

  const { data: entity } = useEntityDetail<Entity>(
    entityConstants.resource,
    selection.size === 1 ? Array.from(selection)[0] : 0,
    undefined,
    { enabled: selection.size === 1 && showModal }
  );

  const signatureTypes = useMemo(() => {
    var hasReviewer = !!entity?.signatures?.find((s) => s.signatureType === "Reviewer");
    return SignatureTypes.filter((t) => t !== "Unknown")
      .filter((t) => !!entity?.signatures?.length || t !== "Reviewer")
      .filter((t) => !hasReviewer || t !== "Contributor")
      .map((t) => ({
        id: t,
        label: t,
        value: t,
      }));
  }, [entity?.signatures]);

  const [approved, setApproved] = useState<boolean>(false);

  useEffect(() => {
    if (!showModal) {
      reset();
      setApproved(false);
    }
    if (!!entity?.signatures && !entity?.signatures?.length)
      setValue("signatureType", { id: "Contributor", label: "Contributor", value: "Contributor" });
    var hasReviewer = !!entity?.signatures?.find((s) => s.signatureType === "Reviewer");
    if (hasReviewer) {
      setValue("signatureType", { id: "Reviewer", label: "Reviewer", value: "Reviewer" });
    }
  }, [entity?.signatures, entity?.signatures?.length, reset, setValue, showModal]);

  if (!entityConstants) return null;
  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        loading={isLoadingBulkSignMutation}
        disabled={selection.size !== 1 || !selectionPermissions?.canSign}
        className="btn btn-block btn-sm btn-ghost-primary flex align-center gap-5"
        title={selection.size !== 1 ? "Adding a signature is only possible for one item at a time" : "Add signature"}
      >
        <LucideIcon name="signature" />
        Add signature
      </Button>

      {showModal && (
        <AlertModal
          showModal={showModal}
          setShowModal={setShowModal}
          type="primary"
          onProceed={handleSign}
          onCancel={() => setShowModal(false)}
          title="Add signature"
          description="Are you sure you want to sign this content? This will prevent other users from editing this content."
          forceUserAuthentication={!isSSOSession}
          children={
            <form
              className={`form-horizontal ${styles.form_holder}`}
              style={{
                margin: 0,
                marginBottom: "5px",
                padding: "0 10px",
                border: "none",
                boxShadow: "none",
                textAlign: "left",
              }}
            >
              <SelectFormField
                id="signatureType"
                label="Signature type"
                items={signatureTypes}
                isDisabled={signatureTypes.length === 1}
                getOptionLabel={(item) => item.label}
                getOptionValue={(item) => item.value}
                errors={errors}
                control={control}
                required
              />

              <TextareaFormField
                id="comment"
                label="Comment"
                errors={errors}
                register={register}
                horizontal
                placeholder="Enter comment..."
                autoFocus={!!isSSOSession}
              />

              {!!currentSignatureType?.value && (
                <>
                  <hr style={{ margin: "10px 0", borderColor: "var(--gray-300)" }} />
                  <label
                    className="flex align-center gap-5"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setApproved((prevState) => !prevState);
                    }}
                  >
                    <div className="col-sm-3 flex align-center justify-center">
                      <ToggleButtonComponent checked={approved}>{null}</ToggleButtonComponent>
                      <span className={styles.required} style={{ color: "var(--danger)" }}>
                        *
                      </span>
                    </div>
                    <h5 style={{ padding: 0, margin: 0, paddingLeft: "10px" }}>
                      {currentSignatureType.value === "Contributor" &&
                        "I confirm that I have contributed to this content and that the content is accurate."}
                      {currentSignatureType.value === "Reviewer" &&
                        "I confirm that I have reviewed this content. All contributors are acknowledged and the content meets the required standards."}
                    </h5>
                  </label>
                  <hr style={{ margin: "10px 0", borderColor: "var(--gray-300)" }} />
                </>
              )}
            </form>
          }
          proceedLabel="Add signature"
          iconOverride="signature"
          proceedDisabled={!currentSignatureType?.value || !approved}
        />
      )}
    </>
  );
};

export const GenericEntityTableUnlockButton = <
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
>({
  entityTypeId,
  selection,
  selectionPermissions,
  currentTab,
}: GenericEntityTableControlProps<Entity, Filters>) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const { entityConstants } = genericEntityConstants[entityTypeId];
  const { bulkUnlockMutationAsync, isLoadingBulkUnlockMutation } = useEntityApi<Entity>(entityConstants.resource);

  const queryClient = useQueryClient();

  const { route, session } = useContext(SessionContext);
  const isSSOSession = session?.type === "jwt";

  const { handleReauth } = useSigninRedirectWithAction();

  const onSuccessCallback = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [entityConstants.resource] });
  }, [entityConstants.resource, queryClient]);

  const {
    register,
    formState: { errors },
    watch,
    reset,
  } = useForm<
    Partial<{
      signatureType: {
        id: Signature["signatureType"];
        label: Signature["signatureType"];
        value: Signature["signatureType"];
      };
      comment: string;
    }>
  >({});
  const currentSignatureType = watch("signatureType");
  const currentComment = watch("comment");

  const handleUnlock = useCallback(
    async (e, props?: AlertModalOnProceedProps) => {
      if (selection.size <= 0) return;
      else if (!!isSSOSession) {
        handleReauth({
          actions: ["Unlock"],
          entityIntIds: Array.from(selection),
          entityType: entityTypeId,
          redirectUrl: route(getIndexRoute(entityConstants.frontendIndexRoute)),
          signature: { comment: currentComment, signatureType: currentSignatureType?.value },
        });
      } else {
        await bulkUnlockMutationAsync(
          {
            ids: Array.from(selection),
            params: { credentials: props?.credentials, signature: { comment: currentComment } },
            showToast: true,
            entityName: entityConstants?.entitySingular,
          },
          { onSuccess: onSuccessCallback }
        ).catch((e) => console.error(e));
      }
      setShowModal(false);
    },
    [
      bulkUnlockMutationAsync,
      currentComment,
      currentSignatureType?.value,
      entityConstants?.entitySingular,
      entityConstants.frontendIndexRoute,
      entityTypeId,
      handleReauth,
      isSSOSession,
      onSuccessCallback,
      route,
      selection,
    ]
  );

  useEffect(() => {
    if (!showModal) {
      reset();
    }
  }, [reset, showModal]);

  if (!entityConstants) return null;
  return (
    <>
      <Button
        onClick={() => setShowModal(true)}
        loading={isLoadingBulkUnlockMutation}
        disabled={!selectionPermissions?.canUnlock}
        className="btn btn-block btn-sm btn-ghost-info flex align-center gap-5"
        title="Unlock selected items"
      >
        <LucideIcon name="lock-open" />
        Unlock
      </Button>

      {showModal && (
        <AlertModal
          showModal={showModal}
          setShowModal={setShowModal}
          type="success"
          onProceed={handleUnlock}
          onCancel={() => setShowModal(false)}
          title="Unlock content"
          description="Are you sure you want to unlock this content? This will remove all signatures and allow other users to edit this content."
          proceedLabel="Unlock"
          iconOverride="lock-open"
          forceUserAuthentication={!isSSOSession}
          children={
            <form
              className={`form-horizontal ${styles.form_holder}`}
              style={{
                margin: 0,
                marginBottom: "5px",
                padding: "0 10px",
                border: "none",
                boxShadow: "none",
                textAlign: "left",
              }}
            >
              <TextareaFormField
                id="comment"
                label="Comment"
                errors={errors}
                register={register}
                horizontal
                placeholder="Enter comment..."
                autoFocus={!!isSSOSession}
              />
            </form>
          }
        />
      )}
    </>
  );
};

interface GenericEntityTableComplianceDropdownProps<
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
> extends GenericEntityTableControlProps<Entity, Filters> {
  showSignButton?: boolean; // Default to false if not provided
}

export const GenericEntityTableComplianceDropdown = <
  Entity extends GenericEntity,
  Filters extends StringIndexedDict & IGenericRequestParameters<Entity, InferSortByOptions<Filters>>,
>({
  entityTypeId,
  selection,
  selectionPermissions,
  tableFunctionRef,
  currentTab,
  filters,
  showSignButton = false, // Default to false if not provided
}: GenericEntityTableComplianceDropdownProps<Entity, Filters>) => {
  if (currentTab === "trash") {
    return <></>; // No compliance actions in the trash tab
  }
  return (
    <NestedDropdown
      label={showSignButton ? "Locking / Signing" : "Locking / Unlocking"}
      icon={"clipboard-check"}
      disabled={selection.size === 0 || selection.size > 1000}
    >
      <li>
        <GenericEntityTableLockButton
          entityTypeId={entityTypeId}
          selection={selection}
          selectionPermissions={selectionPermissions}
          tableFunctionRef={tableFunctionRef}
          currentTab={currentTab}
        />
      </li>
      {showSignButton && (
        <li>
          <GenericEntityTableSignButton
            entityTypeId={entityTypeId}
            selection={selection}
            selectionPermissions={selectionPermissions}
            tableFunctionRef={tableFunctionRef}
            currentTab={currentTab}
          />
        </li>
      )}
      <li>
        <GenericEntityTableUnlockButton
          entityTypeId={entityTypeId}
          selection={selection}
          selectionPermissions={selectionPermissions}
          tableFunctionRef={tableFunctionRef}
          currentTab={currentTab}
        />
      </li>
    </NestedDropdown>
  );
};
